"use client";

import { create } from "zustand";

import { cn } from "@/lib/utils/cn";

/** Lightweight shared filter state for list/detail pages (search, tenant-scoped tags). */
type FilterState = {
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
};

export const useCentralFiltersStore = create<FilterState>((set) => ({
  globalSearch: "",
  setGlobalSearch: (globalSearch) => set({ globalSearch }),
}));

export function CentralizedFiltersBar({ placeholder = "Filter tables…" }: { placeholder?: string }) {
  const value = useCentralFiltersStore((s) => s.globalSearch);
  const set = useCentralFiltersStore((s) => s.setGlobalSearch);

  return (
    <div className="flex items-center gap-ds-2 rounded-[20px] border border-card-border bg-muted-bg/30 px-ds-4 py-ds-3 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
      <label className="sr-only" htmlFor="global-filter">
        Global filter
      </label>
      <input
        id="global-filter"
        value={value}
        onChange={(e) => set(e.target.value)}
        placeholder={placeholder}
        className="ui-control min-w-[12rem] max-w-md flex-1 py-1  text-caption"
      />
    </div>
  );
}

/** Sleek pill search for the page header (center column). */
export const PAGE_HEADER_SEARCH_INPUT_CLASS =
  "ui-control h-10 w-full px-ds-4 text-caption shadow-[0_1px_2px_rgba(15,23,42,0.03)] placeholder:text-muted/55 dark:bg-muted-bg/45 dark:placeholder:text-muted/45";

/** Inline search bound to the shared page filter store — use inside `PageHeader` center column. */
export function PageContextSearchInput({
  placeholder = "Search in page…",
  className,
  id = "page-context-search",
}: {
  placeholder?: string;
  className?: string;
  id?: string;
}) {
  const value = useCentralFiltersStore((s) => s.globalSearch);
  const set = useCentralFiltersStore((s) => s.setGlobalSearch);

  return (
    <>
      <label className="sr-only" htmlFor={id}>
        Filter visible rows
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => set(e.target.value)}
        placeholder={placeholder}
        className={cn(PAGE_HEADER_SEARCH_INPUT_CLASS, className)}
      />
    </>
  );
}
